import type { UserCardStage } from "@-/dev.improve.haniverse.constants.user-card-stage";
import type { GraphemeTaiwanUsageClassification } from "@-/dev.improve.haniverse.data.taiwan-usage-map";
import type { HanziScript } from "@-/dev.improve.haniverse.lib.actor.types";
import { isHskLevelKey, type HskLevelKey } from "@-/dev.improve.haniverse.lib.morpheme.grade.hsk";
import type { TbclLevelKey } from "@-/dev.improve.haniverse.lib.morpheme.grade.tbcl";
import type { GraphemeTier } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.data.prc.tier";

/** A deck row is either a character (grapheme) card or a word card. */
export type CardFilterMorphemeKind = "grapheme" | "word";

/**
 * Shared filter state for the toolbar card-filter menus (deck, browse levels, browse characters).
 *
 * `null` for a set-valued dimension means "no restriction". The simplified-script dimensions (HSK
 * levels, PRC tier) and the traditional-script dimensions (TBCL levels, Taiwan usage
 * classification) are both kept in state so toggling the hanzi-script setting doesn't drop the
 * user's selections; {@link matchesCardFilters} only applies the dimensions relevant to the active
 * script.
 */
export interface CardFilters {
  /** HSK levels to include (applied for the simplified script). */
  hskLevels: ReadonlySet<HskLevelKey> | null;
  /**
   * Which kinds of morpheme to include — characters, words, or both.
   *
   * NOT applied by {@link matchesCardFilters}: every other dimension is decided from
   * {@link CardFilterFacts}, which are keyed by GRAPHEME, and a word card has no grapheme facts
   * (no HSK level, no tier). Surfaces that mix the two apply this dimension against the card's own
   * kind before consulting facts — see the deck page.
   */
  morphemeKinds: ReadonlySet<CardFilterMorphemeKind> | null;
  /** SRS learning stages to include; a grapheme without a user card counts as `"new"`. */
  stages: ReadonlySet<UserCardStage> | null;
  /** TBCL levels to include (applied for the traditional script). */
  tbclLevels: ReadonlySet<TbclLevelKey> | null;
  /** PRC 通用规范汉字表 tiers to include (applied for the simplified script). */
  tiers: ReadonlySet<GraphemeTier> | null;
  /** Taiwan MOE usage classifications to include (applied for the traditional script). */
  usageClassifications: ReadonlySet<GraphemeTaiwanUsageClassification> | null;
}

/**
 * The default filter set: no level/tier/stage/kind restriction.
 */
export function createDefaultCardFilters(overrides: Partial<CardFilters> = {}): CardFilters {
  return {
    hskLevels: null,
    morphemeKinds: null,
    stages: null,
    tbclLevels: null,
    tiers: null,
    usageClassifications: null,
    ...overrides,
  };
}

function areCardFilterSetsEqual<T>(a: ReadonlySet<T> | null, b: ReadonlySet<T> | null): boolean {
  if (a === null || b === null) {
    return a === b;
  }
  if (a.size !== b.size) {
    return false;
  }
  for (const value of a) {
    if (!b.has(value)) {
      return false;
    }
  }
  return true;
}

export function areCardFiltersEqual(a: CardFilters, b: CardFilters): boolean {
  return (
    areCardFilterSetsEqual(a.hskLevels, b.hskLevels) &&
    areCardFilterSetsEqual(a.morphemeKinds, b.morphemeKinds) &&
    areCardFilterSetsEqual(a.stages, b.stages) &&
    areCardFilterSetsEqual(a.tbclLevels, b.tbclLevels) &&
    areCardFilterSetsEqual(a.tiers, b.tiers) &&
    areCardFilterSetsEqual(a.usageClassifications, b.usageClassifications)
  );
}

export function areCardFiltersAtDefault(filters: CardFilters): boolean {
  return areCardFiltersEqual(filters, createDefaultCardFilters());
}

/** The per-grapheme facts {@link matchesCardFilters} evaluates a {@link CardFilters} against. */
export interface CardFilterFacts {
  /**
   * Whether the grapheme card is authored (`version >= 0` frontmatter). Not a filter dimension —
   * kept here so grid/list surfaces can reuse the same batched facts read for authoring display
   * (e.g. fading unauthored tiles).
   */
  completed: boolean;
  /**
   * Raw HSK level for the user's HSK version (`hsk_*_level` column); the static DB stores the 7-9
   * band as `7`. `null` when the grapheme is outside every list of that version.
   */
  hskLevel: number | null;
  /** Current SRS stage; `null` when the grapheme has no user card yet (treated as `"new"`). */
  stage: UserCardStage | null;
  /** Raw TBCL level (`tbcl_level` column); `null` when outside every Taiwan list. */
  tbclLevel: number | null;
  /** PRC tier; `null` when the grapheme is only a decomposition component. */
  tier: GraphemeTier | null;
  /** Taiwan MOE usage classification; `null` when unlisted. */
  usageClassification: GraphemeTaiwanUsageClassification | null;
}

/** The static DB stores the HSK 7-9 band as level `7`; the runtime key for that band is `79`. */
function hskLevelKeyFromRawLevel(level: number): HskLevelKey | null {
  if (isHskLevelKey(level)) {
    return level;
  }
  return level >= 7 && level <= 9 ? 79 : null;
}

/**
 * Whether one grapheme card passes the given filters. Only the dimensions relevant to the active
 * hanzi script are applied: HSK levels + tier for simplified, TBCL levels + usage classification
 * for traditional; the stage dimension always applies. A grapheme with a `null` fact for an active
 * set-valued dimension does not match (e.g. filtering to HSK 1 excludes graphemes outside every HSK
 * list).
 */
export function matchesCardFilters(
  filters: CardFilters,
  facts: CardFilterFacts,
  hanziScript: HanziScript,
): boolean {
  if (filters.stages !== null && !filters.stages.has(facts.stage ?? "new")) {
    return false;
  }
  if (hanziScript === "traditional") {
    if (
      filters.tbclLevels !== null &&
      (facts.tbclLevel === null ||
        !(filters.tbclLevels as ReadonlySet<number>).has(facts.tbclLevel))
    ) {
      return false;
    }
    if (
      filters.usageClassifications !== null &&
      (facts.usageClassification === null ||
        !filters.usageClassifications.has(facts.usageClassification))
    ) {
      return false;
    }
    return true;
  }
  if (filters.hskLevels !== null) {
    const hskLevelKey = facts.hskLevel === null ? null : hskLevelKeyFromRawLevel(facts.hskLevel);
    if (hskLevelKey === null || !filters.hskLevels.has(hskLevelKey)) {
      return false;
    }
  }
  if (
    filters.tiers !== null &&
    (facts.tier === null || !(filters.tiers as ReadonlySet<number>).has(facts.tier))
  ) {
    return false;
  }
  return true;
}

/**
 * Toggle one value of a set-valued filter dimension. `null` (no restriction) toggles into a set of
 * just `value`; a result that is empty or covers every value in `allValues` normalizes back to
 * `null`, so "everything unchecked" and "everything checked" both mean "no restriction".
 */
export function toggleCardFilterValue<T>(
  current: ReadonlySet<T> | null,
  value: T,
  allValues: readonly T[],
): ReadonlySet<T> | null {
  const next = new Set(current);
  if (next.has(value)) {
    next.delete(value);
  } else {
    next.add(value);
  }
  if (next.size === 0 || allValues.every((allValue) => next.has(allValue))) {
    return null;
  }
  return next;
}

/**
 * Does a card of `morphemeKind` pass the morpheme-kind dimension?
 *
 * Separate from {@link matchesCardFilters} because that function is fact-driven and a word card
 * has no grapheme facts to evaluate — callers apply this against the card's own kind first, then
 * consult facts only for graphemes.
 */
export function matchesCardFilterMorphemeKind(
  filters: CardFilters,
  morphemeKind: CardFilterMorphemeKind,
): boolean {
  return filters.morphemeKinds === null || filters.morphemeKinds.has(morphemeKind);
}
